/**
 * Usage analytics for the owner's dashboard (/admin; server: analytics/collect.ts). Sends page views, clicks, the
 * start of a form, and when a page is left how long it was on screen and how far down it was read. No cookies and
 * nothing stored in the browser: the server counts visitors with a hash that changes every day.
 *
 * On the public site a click is recorded with its text. Inside the app only elements marked `data-track` count (the
 * app's Button and ButtonLink, and the sidebar), with the app's own wording, so nothing from someone's books is sent;
 * screens are sent as paths, and the server replaces record IDs in them with `:id`. The owner's own browser is left
 * out once it opens /admin.
 */
import { createBrowserRouter } from 'react-router';
import { COMPARE_PATHS, GUIDE_PATHS } from '../site/articles';
import { sessionHint } from '../site/hooks';

type Router = ReturnType<typeof createBrowserRouter>;
type Area = 'site' | 'app';
interface TrackEvent {
  k: 'pageview' | 'click' | 'leave' | 'form_start';
  a: Area;
  p: string;
  l?: string;
  t?: string;
  r?: string;
  q?: string;
  w?: number;
  d?: number;
  s?: number;
  /** How many milliseconds ago it happened, as the batch leaves: the server dates events by when they happened, not arrived. */
  o?: number;
}

const ENDPOINT = '/api/site/activity';
const OFF = 'raremargin:analytics';

/** The public pages, whose clicks are recorded with their text. */
const MARKETING = new Set<string>(['/', '/pricing', '/terms', '/privacy', '/refunds', '/dpa', '/contact', '/about', '/login', '/signup', '/forgot-password', ...COMPARE_PATHS, ...GUIDE_PATHS]);
/** Pages a signed-out visitor can open: everything here counts as the site, the rest as the app once signed in. */
const PUBLIC = new Set<string>([...MARKETING, '/reset-password', '/accept-invite', '/verify-email', '/connect']);

/** Leaves this browser out of the numbers (the owner's own), or counts it again. */
export const analyticsOff = {
  get: () => {
    try {
      return localStorage.getItem(OFF) === 'off';
    } catch {
      return false;
    }
  },
  set: (off: boolean) => {
    try {
      if (off) localStorage.setItem(OFF, 'off');
      else localStorage.removeItem(OFF);
    } catch {
      // Storage off (private mode): nothing to remember.
    }
  },
};

let queue: { e: TrackEvent; at: number }[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function flush() {
  clearTimeout(timer);
  timer = undefined;
  while (queue.length) {
    const now = Date.now();
    const body = JSON.stringify({ e: queue.splice(0, 40).map(({ e, at }) => ({ ...e, o: now - at })) });
    // A beacon outlives the page, so the last page's time on screen still arrives when the tab closes.
    if (!navigator.sendBeacon?.(ENDPOINT, body)) void fetch(ENDPOINT, { method: 'POST', body, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
  }
}

function send(e: TrackEvent, now = false) {
  if (analyticsOff.get()) return;
  queue.push({ e, at: Date.now() });
  if (now) flush();
  else timer ??= setTimeout(flush, 1000);
}

const areaOf = (path: string): Area => (!sessionHint.get() || (path !== '/' && PUBLIC.has(path)) ? 'site' : 'app');

/** The page on screen: how long it has been visible, and the furthest it was scrolled. */
let page: { path: string; area: Area; view: TrackEvent; at: number; shown: number; since: number | null; scroll: number; clicked: boolean; formStarted: boolean } | null = null;

function scrollDepth(target?: EventTarget | null) {
  const el = target instanceof Element && target.tagName === 'MAIN' ? target : (document.querySelector('main.overflow-y-auto') ?? document.scrollingElement);
  if (!el) return 100;
  const top = el === document.scrollingElement ? window.scrollY : el.scrollTop;
  const height = el === document.scrollingElement ? window.innerHeight : el.clientHeight;
  return el.scrollHeight <= height ? 100 : Math.min(100, Math.round(((top + height) / el.scrollHeight) * 100));
}

/** Sends the time on screen so far and the scroll depth, then starts counting again from zero. */
function reportLeave(now: boolean) {
  if (!page) return;
  if (page.since !== null) page.shown += performance.now() - page.since;
  page.since = document.visibilityState === 'visible' ? performance.now() : null;
  const d = Math.round(page.shown);
  page.shown = 0;
  page.scroll = Math.max(page.scroll, scrollDepth());
  if (d > 0) send({ k: 'leave', a: page.area, p: page.path, d, s: page.scroll }, now);
}

/** Where this page load came from, sent with its first page view (the referrer stays the same for the whole visit). */
let landing: { r: string; q: string } | null = null;

function view(path: string) {
  if (page && page.path === path) return;
  const t = performance.now();
  // A page left within a moment, untouched, was a redirect (an app page sending a visitor to sign in): not a view.
  if (page && t - page.at < 400 && !page.clicked && queue.some((q) => q.e === page!.view)) {
    queue = queue.filter((q) => q.e !== page!.view);
    if (page.view.r !== undefined) landing = { r: page.view.r, q: page.view.q ?? '' };
  } else reportLeave(false);
  const area = areaOf(path);
  const e: TrackEvent = { k: 'pageview', a: area, p: path, w: window.innerWidth };
  if (landing) Object.assign(e, landing);
  landing = null;
  page = { path, area, view: e, at: t, shown: 0, since: document.visibilityState === 'visible' ? t : null, scroll: 0, clicked: false, formStarted: false };
  send(e);
  requestAnimationFrame(() => page && (page.scroll = Math.max(page.scroll, scrollDepth())));
}

/** A control's name when its own text doesn't give one: aria-label, then its <label> (the first line, not the description). */
function labelOf(el: Element) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const label = el instanceof HTMLButtonElement || el instanceof HTMLInputElement ? el.labels?.[0] : null;
  return (label?.firstElementChild ?? label)?.textContent || null;
}

function onClick(ev: MouseEvent) {
  if (!page) return;
  const target = ev.target instanceof Element ? ev.target : null;
  const el = page.area === 'app' || !MARKETING.has(page.path) ? target?.closest('[data-track]') : target?.closest('a, button, [role="button"], [data-track], summary');
  if (!el) return;
  page.clicked = true;
  let label = el.getAttribute('data-track') || (page.area === 'site' ? labelOf(el) : null) || el.textContent || '';
  // A switch: which way it was turned (the click hasn't changed it yet).
  if (el.getAttribute('role') === 'switch') label += el.getAttribute('aria-checked') === 'true' ? ' → off' : ' → on';
  const href = el instanceof HTMLAnchorElement ? el.href : undefined;
  // A link to another site unloads this page: send at once.
  send({ k: 'click', a: page.area, p: page.path, l: label.replace(/\s+/g, ' ').trim().slice(0, 200), t: href }, !!href && new URL(href, location.href).host !== location.host);
}

function onFocus(ev: FocusEvent) {
  if (!page || page.formStarted || page.area !== 'site') return;
  const el = ev.target;
  if (el instanceof HTMLElement && el.closest('form') && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) {
    page.formStarted = true;
    send({ k: 'form_start', a: page.area, p: page.path });
  }
}

let ticking = false;
function onScroll(ev: Event) {
  if (ticking || !page) return;
  ticking = true;
  requestAnimationFrame(() => {
    ticking = false;
    if (page) page.scroll = Math.max(page.scroll, scrollDepth(ev.target));
  });
}

/** Starts counting: the first page, then each new path the router shows. */
export function startAnalytics(router: Router) {
  if (typeof window === 'undefined') return;
  landing = { r: document.referrer, q: location.search };
  view(router.state.location.pathname);
  router.subscribe((state) => view(state.location.pathname));
  document.addEventListener('click', onClick, true);
  document.addEventListener('focusin', onFocus, true);
  // Scroll events don't bubble, but they pass the document on the way down: this sees the app's <main> as well.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') reportLeave(true);
    else if (page && page.since === null) page.since = performance.now();
  });
  window.addEventListener('pagehide', () => reportLeave(true));
}
